import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Menu, X, Moon, Sun, User, LogOut, Settings as SettingsIcon, LayoutDashboardIcon, Puzzle } from "lucide-react";
import { useTheme } from "next-themes";
import { cn } from "../../lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

const API_URL = process.env.REACT_APP_API_URL;
const API_ORIGIN = (() => {
  try {
    return new URL(API_URL).origin;
  } catch (error) {
    return "";
  }
})();

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch (error) {
    return null;
  }
};

const storeUser = (user) => {
  const nextUser = JSON.stringify(user);
  if (localStorage.getItem("user") !== nextUser) {
    localStorage.setItem("user", nextUser);
  }
};

const getProfileImageSrc = (profileImage) => {
  if (!profileImage) return "";
  if (profileImage.startsWith?.("http") || profileImage.startsWith?.("data:") || profileImage.startsWith?.("blob:")) {
    return profileImage;
  }

  const cleanPath = profileImage.replace(/^\/+/, "");
  if (cleanPath.startsWith("uploads/")) {
    return `${API_ORIGIN}/${cleanPath}`;
  }

  return `${API_ORIGIN}/uploads/${cleanPath}`;
};

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("token"));
  const [currentUser, setCurrentUser] = useState(() => getStoredUser());
    

  useEffect(() => {
    let isMounted = true;

    const fetchUserIfMissing = async (token) => {
      if (!token || getStoredUser()) return;

      try {
        const response = await fetch(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!isMounted) return;

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            setIsLoggedIn(false);
            setCurrentUser(null);
          }
          return;
        }

        const data = await response.json();
        if (data.success && data.data) {
          setCurrentUser(data.data);
          storeUser(data.data);
        }
      } catch (error) {
        // Keep the navbar usable with token-only auth if the profile fetch fails.
      }
    };

    const syncAuthState = () => {
      const token = localStorage.getItem("token");
      const storedUser = getStoredUser();

      setIsLoggedIn(!!token);
      setCurrentUser(token ? storedUser : null);

      if (!token) {
        return;
      }

      fetchUserIfMissing(token);
    };

    syncAuthState();
    const handleStorage = (event) => {
      if (event.key === "token" || event.key === "user") {
        syncAuthState();
      }
    };
    const handleAuthUpdate = () => syncAuthState();

    window.addEventListener("storage", handleStorage);
    window.addEventListener("hannacode:user-updated", handleAuthUpdate);

    return () => {
      isMounted = false;
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("hannacode:user-updated", handleAuthUpdate);
    };
  }, [location.pathname]);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setCurrentUser(null);
    setIsLoggedIn(false);
    navigate("/login");
  };

  const userInitial = currentUser?.name?.charAt(0)?.toUpperCase() || currentUser?.email?.charAt(0)?.toUpperCase() || "U";
  const avatarSrc = getProfileImageSrc(currentUser?.profileImage);

  const navItems = [
    { name: "Home", path: "/" },
    { name: "Courses", path: "/courses" },
    { name: "Pricing", path: "/pricing" },
    { name: "About", path: "/about" },
    ...(isLoggedIn ? [{ name: "Blog", path: "/blog" }] : []),
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center gap-0">
            <img
          src="/Background1.png" 
          alt="HannaCode Logo"
          className="h-14 w-auto bg-transparent" 
        />
            <span className="text-2xl font-bold mt-2">HannaCode </span>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "text-sm font-medium transition-colors hover:text-primary",
                pathname === item.path ? "text-primary" : "text-muted-foreground"
              )}
            >
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {/* Theme Toggle */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="rounded-full">
                {theme === "dark" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                <span className="sr-only">Toggle theme</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setTheme("light")}>Light</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme("dark")}>Dark</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme("system")}>System</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Auth Buttons Desktop */}
          <div className="hidden md:flex items-center gap-4">
            {!isLoggedIn ? (
              <>
                <Link to="/login">
                  <Button variant="outline">Log in</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-primary hover:bg-primary/90">Sign up</Button>
                </Link>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="rounded-full p-0">
                    <Avatar className="h-9 w-9">
                      <AvatarImage className="object-cover" src={avatarSrc} alt={currentUser?.name || "Profile"} />
                      <AvatarFallback className="text-xs font-medium">{userInitial}</AvatarFallback>
                    </Avatar>
                    <span className="sr-only">Open account menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/profile">
                      <User className="mr-2 h-4 w-4" /> Profile
                    </Link>
                  </DropdownMenuItem>
                 
                   <DropdownMenuItem asChild>
                    <Link to="/dashboard">
                      <LayoutDashboardIcon className="mr-2 h-4 w-4" /> dashboard
                    </Link>
                  </DropdownMenuItem>

                   <DropdownMenuItem asChild>
                    <Link to="/settings">
                      <SettingsIcon className="mr-2 h-4 w-4" /> Settings
                    </Link>
                  </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                    <Link to="/challenges">
                      <Puzzle className="mr-2 h-4 w-4" /> Challenges
                    </Link>
                  </DropdownMenuItem>

                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button variant="ghost" size="icon" className="md:hidden" onClick={toggleMenu}>
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            <span className="sr-only">Toggle menu</span>
          </Button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden border-t">
          <div className="container py-4 flex flex-col gap-4">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "text-sm font-medium transition-colors hover:text-primary py-2",
                  pathname === item.path ? "text-primary" : "text-muted-foreground"
                )}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <div className="flex flex-col gap-2 pt-2 border-t">
              {!isLoggedIn ? (
                <>
                  <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="outline" className="w-full">
                      Log in
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                    <Button className="w-full bg-primary hover:bg-primary/90">Sign up</Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/profile" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="outline" className="w-full flex items-center justify-start">
                      <Avatar className="mr-2 h-6 w-6">
                        <AvatarImage className="object-cover" src={avatarSrc} alt={currentUser?.name || "Profile"} />
                        <AvatarFallback className="text-[10px] font-medium">{userInitial}</AvatarFallback>
                      </Avatar>
                      Profile
                    </Button>
                  </Link>
                  <Link to="/dashboard" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="outline" className="w-full flex items-center justify-start">
                      <LayoutDashboardIcon className="mr-2 h-4 w-4" /> Dashboard
                    </Button>
                  </Link>
                  <Link to="/settings" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="outline" className="w-full flex items-center justify-start">
                      <SettingsIcon className="mr-2 h-4 w-4" /> Settings
                    </Button>
                  </Link>
                  <Link to="/challenges" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="outline" className="w-full flex items-center justify-start">
                      <Puzzle className="mr-2 h-4 w-4" /> Challenges
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    className="w-full flex items-center justify-start"
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                  >
                    <LogOut className="mr-2 h-4 w-4" /> Logout
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
